import { useDebounce } from '@uidotdev/usehooks'
import { Modal } from 'antd'
import axios, { isAxiosError } from 'axios'
import type { ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useInView } from 'react-intersection-observer'
import { toast } from 'sonner'

import {
  clearImageCache,
  updateImageCache,
} from '@/components/Atoms/Image/Image'
import { DELAY } from '@/constants'
import { useTreeTilesStore } from '@/stores/feed/treeTilesStore'
import type { AntdTreeType } from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import {
  fetchBrands,
  fetchCategoriesTree,
  fetchCountries,
  fetchNomenclatureGroupsTree,
  fetchUnitsOfMeasurement,
} from '@/utils/api/fetch/fetchEntities'
import {
  applySupplierArticlesAction,
  cleanSupplierArticles,
  collectSupplierArticles,
  getDisplaySupplierArticles,
  getSupplierArticlesError,
  toNormalizedArticle,
} from '@/utils/nomenclature/articles'
import { getBarcodeError, sanitizeBarcode } from '@/utils/nomenclature/barcode'
import {
  buildSlotValuesPayload,
  DYNAMIC_SLOTS_LIMIT,
  findSlotGap,
  formatSlotsValue,
  formatSlotValuesList,
  getDynamicSlotsCount,
  getSlotsCount,
  isSlottedCharacteristicType,
  isUniformSlottedEligible,
  resolveSlotsCount,
  slotsToFormValueIds,
} from '@/utils/search/characteristics/slottedCharacteristics'
import { confirmSlottedImpact } from '@/utils/search/characteristics/slottedImpactPreview'
import convertToAntdFormatTree from '@/utils/tree/antd/convertToAntdFormatTree'
import { findNodeById } from '@/utils/tree/nodes/findNodeById'

import { fetchCharacteristicDetails } from '../shared/characteristicDetailsFetcher'
import {
  type ActiveCharacteristicFilter,
  type ActiveTab,
  type AttachFile,
  type AttachmentType,
  type AttributeProcessingContextValue,
  type AvailableCharacteristic,
  type BackendSort,
  type BatchNomenclatureDetailsResponse,
  type Characteristic,
  type CharacteristicListResponse,
  type CharacteristicValuesResponse,
  type DictionaryValue,
  type EmptyCharacteristicsFilter,
  getMainFieldDisplayValue,
  getMainFieldIdValue,
  getMainFieldListValues,
  MAIN_FIELDS,
  type MassUpdateAction,
  type MassUpdateRequest,
  type NomenclatureAdminSearchResponse,
  type NomenclatureDetailsItem,
  type NomenclatureExtendedModel,
  type NomenclatureRow,
  type PendingFile,
  type SimpleEntity,
} from './AttributeProcessing.types'
import {
  buildAdminSearchRequest,
  DEFAULT_EMPTY_CHARACTERISTICS_FILTER,
  hasEmptyCharacteristicsFilter,
  toNomenclatureRows,
} from './lib/attributeProcessingSearch'

const PAGE_SIZE = 30

const AttributeProcessingContext =
  createContext<AttributeProcessingContextValue | null>(null)

export function useAttributeProcessingContext(): AttributeProcessingContextValue {
  const ctx = useContext(AttributeProcessingContext)
  if (!ctx)
    throw new Error(
      'useAttributeProcessingContext must be used inside AttributeProcessingProvider'
    )
  return ctx
}

export function AttributeProcessingProvider({
  children,
}: {
  children: ReactNode
}) {
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query, DELAY)
  const [barcode, setBarcode] = useState('')
  const [brandId, setBrandId] = useState<string>('')
  const [categoryId, setCategoryId] = useState<string>('')
  const [parentId, setParentId] = useState<string>('')
  const [countryIds, setCountryIds] = useState<string[]>([])
  const [unitIds, setUnitIds] = useState<string[]>([])
  const [isDiscontinued, setIsDiscontinued] = useState<boolean | null>(null)
  const [isFilled, setIsFilled] = useState<boolean | null>(null)
  const [multiplicity, setMultiplicity] = useState<boolean | null>(null)
  const [emptyCharacteristicsFilter, setEmptyCharacteristicsFilter] =
    useState<EmptyCharacteristicsFilter>(DEFAULT_EMPTY_CHARACTERISTICS_FILTER)
  const [showFilters, setShowFilters] = useState(true)

  const [availableCharacteristics, setAvailableCharacteristics] = useState<
    AvailableCharacteristic[]
  >([])
  const [characteristicsLoading, setCharacteristicsLoading] = useState(false)
  const [activeChrcFilters, setActiveChrcFilters] = useState<
    ActiveCharacteristicFilter[]
  >([])
  const [addFilterOpen, setAddFilterOpen] = useState(false)

  const [rows, setRows] = useState<NomenclatureRow[]>([])
  const [backendSort, setBackendSort] = useState<BackendSort>({
    article: null,
    description: null,
  })
  const [dynamicSortDir, setDynamicSortDir] = useState<'asc' | 'desc' | null>(
    null
  )
  const backendSortRef = useRef<BackendSort>({
    article: null,
    description: null,
  })
  const selectedRowsCacheRef = useRef<Map<string, NomenclatureRow>>(new Map())
  const [totalRows, setTotalRows] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const hasSearched = useRef(false)

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const lastClickedIndex = useRef<number | null>(null)

  const [selectedDetails, setSelectedDetails] = useState<
    NomenclatureDetailsItem[]
  >([])
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [imageReady, setImageReady] = useState(false)

  const [activeTab, setActiveTab] = useState<ActiveTab>('ADDITIONAL')
  const [selectedMainField, setSelectedMainField] = useState<string | null>(
    null
  )
  const [selectedChrcCode, setSelectedChrcCode] = useState<number | null>(null)
  const [action, setAction] = useState<MassUpdateAction>('SET')
  const [processValue, setProcessValue] = useState<string>('')
  const [processValues, setProcessValues] = useState<string[]>([])
  // Значения присоединений для SLOTTED_REFERENCE: индекс массива = слот - 1.
  // Хранятся valueId справочника (записка §4a), не текстовые литералы.
  const [processSlotValues, setProcessSlotValues] = useState<string[]>([])
  // Разрешённый лимит присоединений для выбранной характеристики и признак того,
  // что у выбранных товаров он разный
  const [processSlotsCount, setProcessSlotsCount] = useState<number>(0)
  const [processSlotsMismatch, setProcessSlotsMismatch] = useState(false)
  // Категория не задала slotsCount ни для одного из выбранных товаров:
  // рендерим поля динамически (записка «Значение по умолчанию для slotsCount»),
  // а не по жёсткому лимиту.
  const [processSlotsDynamic, setProcessSlotsDynamic] = useState(false)

  const chrcSelectRequestRef = useRef(0)

  const resetSlottedDraft = useCallback(() => {
    chrcSelectRequestRef.current += 1
    setProcessSlotValues([])
    setProcessSlotsCount(0)
    setProcessSlotsMismatch(false)
    setProcessSlotsDynamic(false)
  }, [])

  // В динамическом режиме количество полей растёт вместе со значениями —
  // держим processSlotsCount в синхроне с длиной массива, иначе
  // buildSlotValuesPayload обрежет только что добавленное значение.
  useEffect(() => {
    if (processSlotsDynamic) setProcessSlotsCount(processSlotValues.length)
  }, [processSlotsDynamic, processSlotValues.length])

  /**
   * Обновляет присоединение по индексу. Очистка присоединения очищает и все
   * последующие (запрещённый пропуск), а заполнение последнего пустого поля
   * в динамическом режиме добавляет следующее пустое — до системного лимита
   * (записка «Значение по умолчанию для slotsCount»).
   */
  const updateSlotValue = useCallback(
    (index: number, next: string) => {
      setProcessSlotValues((prev) => {
        const updated = [...prev]
        updated[index] = next ?? ''

        if (!updated[index]) {
          for (let i = index + 1; i < updated.length; i += 1) updated[i] = ''
        } else if (
          processSlotsDynamic &&
          index === updated.length - 1 &&
          updated.length < DYNAMIC_SLOTS_LIMIT
        ) {
          updated.push('')
        }

        return updated
      })
    },
    [processSlotsDynamic]
  )
  const [dynamicColumnLabel, setDynamicColumnLabel] =
    useState('Выбранный реквизит')

  const [brands, setBrands] = useState<SimpleEntity[]>([])
  const [brandsLoading, setBrandsLoading] = useState(false)
  const [countries, setCountries] = useState<SimpleEntity[]>([])
  const [countriesLoading, setCountriesLoading] = useState(false)
  const [units, setUnits] = useState<SimpleEntity[]>([])
  const [unitsLoading, setUnitsLoading] = useState(false)
  const [categoriesLoading, setCategoriesLoading] = useState(false)
  const [parentsLoading, setParentsLoading] = useState(false)
  const [antdCategoriesTreeLeafOnly, setAntdCategoriesTreeLeafOnly] = useState<
    AntdTreeType[]
  >([])
  const [antdParentsTreeLeafOnly, setAntdParentsTreeLeafOnly] = useState<
    AntdTreeType[]
  >([])
  const [dictionaryValues, setDictionaryValues] = useState<DictionaryValue[]>(
    []
  )
  const [dictionaryLoading, setDictionaryLoading] = useState(false)
  const [processing, setProcessing] = useState(false)

  const [attachDocs, setAttachDocs] = useState<AttachFile[]>([])
  const [attachDocsLoading, setAttachDocsLoading] = useState(false)
  const [attachDocsFetched, setAttachDocsFetched] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  const [mainPendingImageId, setMainPendingImageId] = useState<string | null>(
    null
  )
  const [imageVersion, setImageVersion] = useState(0)

  const [modal, contextHolder] = Modal.useModal()

  const {
    nomenclatureGroupsTree: globalGroupsTree,
    categoriesTree: globalCategoriesTree,
    setNomenclatureGroupsTree,
    setCategoriesTree,
  } = useTreeTilesStore()

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.1,
    rootMargin: '200px',
  })

  const availableCharacteristicsRef = useRef<AvailableCharacteristic[]>([])
  const rowsRef = useRef<NomenclatureRow[]>([])
  const totalRowsRef = useRef(0)
  const pageRef = useRef(0)
  const loadingRef = useRef(false)
  const loadingMoreRef = useRef(false)
  useEffect(() => {
    rowsRef.current = rows
  }, [rows])
  useEffect(() => {
    totalRowsRef.current = totalRows
  }, [totalRows])
  useEffect(() => {
    pageRef.current = page
  }, [page])
  useEffect(() => {
    loadingRef.current = loading
  }, [loading])
  useEffect(() => {
    loadingMoreRef.current = loadingMore
  }, [loadingMore])
  useEffect(() => {
    backendSortRef.current = backendSort
  }, [backendSort])

  const detailsAbortRef = useRef<AbortController | null>(null)

  const cacheRows = useCallback((items: NomenclatureRow[]) => {
    items.forEach((row) => selectedRowsCacheRef.current.set(row.id, row))
  }, [])

  useEffect(() => {
    setPendingFiles((prev) => {
      prev.forEach((f) => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl)
      })
      return []
    })
    setMainPendingImageId(null)
    setAttachDocs([])
    setAttachDocsFetched(false)
  }, [selectedIds])

  useEffect(() => {
    const singleCode =
      selectedIds.size === 1 &&
      selectedDetails.length === 1 &&
      selectedIds.has(selectedDetails[0].id)
        ? selectedDetails[0].code
        : null

    if (!singleCode) return

    if (activeTab === 'ATTACHMENTS' && !attachDocsFetched) {
      setAttachDocsLoading(true)
      axios
        .get(`/tunnel/evan/pipss/hs/v1/documents/${singleCode}`)
        .then((res) => {
          setAttachDocs(res.data?.file || [])
          setAttachDocsFetched(true)
        })
        .catch(() =>
          toast.error(
            `Ошибка загрузки документов для «${selectedDetails[0]?.nomenclatureExtendedModel?.description ?? singleCode}»`
          )
        )
        .finally(() => setAttachDocsLoading(false))
    }
  }, [activeTab, selectedIds, selectedDetails, attachDocsFetched])

  const brandsLoadedRef = useRef(false)
  const countriesLoadedRef = useRef(false)
  const unitsLoadedRef = useRef(false)
  const categoriesLoadedRef = useRef(false)
  const parentsLoadedRef = useRef(false)

  const loadBrands = async () => {
    if (brandsLoadedRef.current) return
    brandsLoadedRef.current = true
    setBrandsLoading(true)
    try {
      const b = await fetchBrands()
      setBrands(b.map((x) => ({ id: x.id, description: x.description })))
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setBrandsLoading(false)
    }
  }

  const loadCountries = async () => {
    if (countriesLoadedRef.current) return
    countriesLoadedRef.current = true
    setCountriesLoading(true)
    try {
      const c = await fetchCountries()
      setCountries(c.map((x) => ({ id: x.id, description: x.description })))
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setCountriesLoading(false)
    }
  }

  const loadUnits = async () => {
    if (unitsLoadedRef.current) return
    unitsLoadedRef.current = true
    setUnitsLoading(true)
    try {
      const u = await fetchUnitsOfMeasurement()
      setUnits(u.map((x) => ({ id: x.id, description: x.description })))
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setUnitsLoading(false)
    }
  }

  const loadCategories = async (force = false) => {
    if (!force && categoriesLoadedRef.current) return
    categoriesLoadedRef.current = true
    setCategoriesLoading(true)
    try {
      const catsTree =
        force || globalCategoriesTree.length === 0
          ? await fetchCategoriesTree()
          : globalCategoriesTree
      if (force || globalCategoriesTree.length === 0)
        setCategoriesTree(catsTree)
      const cfg = { allSelectable: false, inTreeSelect: true, classNames: {} }
      setAntdCategoriesTreeLeafOnly(
        convertToAntdFormatTree({ nodes: catsTree, ...cfg })
      )
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setCategoriesLoading(false)
    }
  }

  const loadParents = async () => {
    if (parentsLoadedRef.current) return
    parentsLoadedRef.current = true
    setParentsLoading(true)
    try {
      const groupsTree =
        globalGroupsTree.length === 0
          ? await fetchNomenclatureGroupsTree()
          : globalGroupsTree
      if (globalGroupsTree.length === 0) setNomenclatureGroupsTree(groupsTree)
      const cfg = { allSelectable: false, inTreeSelect: true, classNames: {} }
      setAntdParentsTreeLeafOnly(
        convertToAntdFormatTree({ nodes: groupsTree, ...cfg })
      )
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setParentsLoading(false)
    }
  }

  const characteristicsLoadedRef = useRef(false)

  const loadCharacteristicsTree = async (
    force = false
  ): Promise<AvailableCharacteristic[]> => {
    if (!force && characteristicsLoadedRef.current)
      return availableCharacteristicsRef.current
    characteristicsLoadedRef.current = true
    setCharacteristicsLoading(true)
    try {
      const res = await api.get<CharacteristicListResponse>(
        '/main/characteristics'
      )
      const items = res.data.characteristics ?? []
      const mapped: AvailableCharacteristic[] = items.map((c) => ({
        id: c.id,
        code: c.code,
        name: c.description,
        type: c.valueType,
        unit: c.unitOfMeasurementDto?.description,
        unitId: c.unitOfMeasurementDto?.id,
        status: c.status,
        options: [],
      }))
      setAvailableCharacteristics(mapped)
      availableCharacteristicsRef.current = mapped
      return mapped
    } catch (error) {
      handleApiError(error, modal)
      return []
    } finally {
      setCharacteristicsLoading(false)
    }
  }

  const refreshCategoriesTree = async () => {
    categoriesLoadedRef.current = false
    await loadCategories(true)
  }

  const refreshCharacteristicsTree = async () => {
    characteristicsLoadedRef.current = false
    return loadCharacteristicsTree(true)
  }

  const addCharacteristicFilter = async (code: number) => {
    const chrc = availableCharacteristics.find((c) => c.code === code)
    if (!chrc) return
    if (activeChrcFilters.some((f) => f.code === code)) return

    // Слотовая характеристика («Диаметр присоединения») заполняется из того же
    // справочника значений, что и REFERENCE, — справочник грузим и для неё.
    const needsDictionary =
      chrc.type === 'REFERENCE' || isSlottedCharacteristicType(chrc.type)

    const filterId = `${code}-${Date.now()}`
    setActiveChrcFilters((prev) => [
      ...prev,
      {
        id: filterId,
        code,
        name: chrc.name,
        type: chrc.type,
        unit: chrc.unit,
        options: [],
        referenceOptions: undefined,
        optionsLoading: needsDictionary,
        chosenOptions: [],
        rangeMin: '',
        rangeMax: '',
        slotRows: 1,
      },
    ])
    setAddFilterOpen(false)

    if (needsDictionary) {
      try {
        const details = await fetchCharacteristicDetails(chrc.id)
        const dictValues = details.items
        setActiveChrcFilters((prev) =>
          prev.map((f) =>
            f.id === filterId
              ? {
                  ...f,
                  referenceOptions: dictValues.map((d) => ({
                    id: d.id,
                    value: d.value,
                  })),
                  optionsLoading: false,
                }
              : f
          )
        )
      } catch (error) {
        handleApiError(error, modal)
        setActiveChrcFilters((prev) =>
          prev.map((f) =>
            f.id === filterId ? { ...f, optionsLoading: false } : f
          )
        )
      }
    }
  }

  const removeCharacteristicFilter = (id: string) => {
    setActiveChrcFilters((prev) => prev.filter((f) => f.id !== id))
  }

  const updateChrcFilter = (
    id: string,
    patch: Partial<ActiveCharacteristicFilter>
  ) => {
    const matchUniformPatch = patch.matchUniform
    setActiveChrcFilters((prev) =>
      prev.map((f) => {
        const isTarget = f.id === id
        const syncUniform =
          matchUniformPatch !== undefined && isSlottedCharacteristicType(f.type)
        if (!isTarget && !syncUniform) return f
        const next = isTarget ? { ...f, ...patch } : { ...f }
        if (syncUniform) next.matchUniform = matchUniformPatch
        if (
          next.matchUniform &&
          !isUniformSlottedEligible(next.chosenOptions)
        ) {
          next.matchUniform = false
        }
        return next
      })
    )
  }

  const resetAllFilters = () => {
    setBrandId('')
    setCategoryId('')
    setParentId('')
    setCountryIds([])
    setUnitIds([])
    setBarcode('')
    setIsDiscontinued(null)
    setIsFilled(null)
    setMultiplicity(null)
    setActiveChrcFilters([])
    setEmptyCharacteristicsFilter(DEFAULT_EMPTY_CHARACTERISTICS_FILTER)
  }

  const hasActiveFilters =
    brandId !== '' ||
    categoryId !== '' ||
    parentId !== '' ||
    countryIds.length > 0 ||
    unitIds.length > 0 ||
    barcode.trim().length > 0 ||
    isDiscontinued !== null ||
    isFilled !== null ||
    multiplicity !== null ||
    activeChrcFilters.length > 0 ||
    hasEmptyCharacteristicsFilter(emptyCharacteristicsFilter)

  const doSearch = useCallback(
    async (
      searchPage: number,
      replace: boolean,
      queryOverride?: string,
      sortOverride?: BackendSort
    ) => {
      if (replace) setLoading(true)
      else setLoadingMore(true)

      try {
        const response = await api.post<NomenclatureAdminSearchResponse>(
          '/main/nomenclatures/admin/search',
          buildAdminSearchRequest({
            page: searchPage,
            pageSize: PAGE_SIZE,
            query: queryOverride !== undefined ? queryOverride : debouncedQuery,
            barcode,
            brandId,
            categoryId,
            parentId,
            countryIds,
            unitIds,
            isDiscontinued,
            isFilled,
            multiplicity,
            sort: sortOverride ?? backendSortRef.current,
            characteristicFilters: activeChrcFilters,
            emptyFilter: emptyCharacteristicsFilter,
            characteristicTypes: new Map(
              availableCharacteristics.map((chrc) => [chrc.code, chrc.type])
            ),
          })
        )

        const newRows = toNomenclatureRows(response.data)
        const total = response.data.nomenclatures.totalElements

        cacheRows(newRows)
        const nextRows = replace ? newRows : [...rowsRef.current, ...newRows]
        if (replace) {
          setRows(newRows)
        } else {
          setRows(nextRows)
        }
        setTotalRows(total)
        setPage(searchPage + 1)
        hasSearched.current = true
      } catch (error) {
        handleApiError(error, modal)
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [
      debouncedQuery,
      barcode,
      brandId,
      categoryId,
      parentId,
      countryIds,
      unitIds,
      isDiscontinued,
      isFilled,
      multiplicity,
      activeChrcFilters,
      emptyCharacteristicsFilter,
      availableCharacteristics,
      cacheRows,
    ]
  )

  const handleBackendSort = useCallback(
    (col: keyof BackendSort) => {
      const prev = backendSortRef.current
      const next: BackendSort = { ...prev }
      if (next[col] === null) next[col] = 'desc'
      else if (next[col] === 'desc') next[col] = 'asc'
      else next[col] = null
      backendSortRef.current = next
      setBackendSort(next)
      if (hasSearched.current && !loadingRef.current) {
        setRows([])
        setPage(0)
        doSearch(0, true, query, next)
      }
    },
    [doSearch, query]
  )

  const handleDynamicSort = useCallback(() => {
    setDynamicSortDir((prev) => {
      if (prev === null) return 'desc'
      if (prev === 'desc') return 'asc'
      return null
    })
  }, [])

  const handleSearch = useCallback(() => {
    if (loadingRef.current) return
    if (!hasActiveFilters && !query.trim()) return
    setRows([])
    setSelectedIds(new Set())
    setSelectedDetails([])
    setPage(0)
    doSearch(0, true, query)
  }, [doSearch, query, hasActiveFilters])

  useEffect(() => {
    if (
      inView &&
      !loadingRef.current &&
      !loadingMoreRef.current &&
      rowsRef.current.length < totalRowsRef.current &&
      rowsRef.current.length > 0
    ) {
      doSearch(pageRef.current, false)
    }
  }, [inView, doSearch])

  useEffect(() => {
    setImageReady(false)

    if (selectedIds.size === 0) {
      selectedRowsCacheRef.current.clear()
      setSelectedDetails([])
      setRows((prev) => prev.map((r) => ({ ...r, dynamicValue: '' })))
      setDynamicColumnLabel('Выбранный реквизит')
      setActiveTab('ADDITIONAL')
      setSelectedMainField(null)
      setSelectedChrcCode(null)
      setProcessValue('')
      setProcessValues([])
      resetSlottedDraft()
      setAction('SET')
      setDictionaryValues([])
      return
    }

    setSelectedChrcCode(null)
    setDictionaryValues([])
    setProcessValue('')
    setProcessValues([])
    resetSlottedDraft()
    setDynamicColumnLabel((prev) =>
      MAIN_FIELDS.some((f) => f.label === prev) ? prev : 'Выбранный реквизит'
    )
    setRows((prev) => prev.map((r) => ({ ...r, dynamicValue: '' })))

    const rowsById = new Map(rows.map((row) => [row.id, row]))
    const selectedRows = Array.from(selectedIds)
      .map((id) => selectedRowsCacheRef.current.get(id) ?? rowsById.get(id))
      .filter((row): row is NomenclatureRow => Boolean(row))
    const codes = selectedRows.map((r) => r.code)

    detailsAbortRef.current?.abort()
    const controller = new AbortController()
    detailsAbortRef.current = controller

    setDetailsLoading(true)
    api
      .post<BatchNomenclatureDetailsResponse>(
        '/main/nomenclatures/details/batch-extended',
        { codes },
        { signal: controller.signal }
      )
      .then((response) => {
        const items = response.data.items ?? []
        const details: NomenclatureDetailsItem[] = items.map((item) => {
          const model =
            item.details?.nomenclatureExtendedModel ??
            item.nomenclatureExtendedModel ??
            ({} as NomenclatureExtendedModel)
          const detailsText: string = item.details?.details ?? ''
          const chars: Characteristic[] =
            item.details?.characteristics ?? item.characteristics ?? []
          return {
            id: model.id,
            code: model.code,
            nomenclatureExtendedModel: { ...model, details: detailsText },
            characteristics: chars,
          }
        })
        setSelectedDetails(details)

        if (details.length === 1 && selectedMainField) {
          const field = MAIN_FIELDS.find((f) => f.key === selectedMainField)
          if (field && !field.readonly) {
            const model = details[0].nomenclatureExtendedModel
            setProcessValue(
              field.type.startsWith('REFERENCE_')
                ? getMainFieldIdValue(model, selectedMainField)
                : getMainFieldDisplayValue(model, selectedMainField)
            )
            setProcessValues(getMainFieldListValues(model, selectedMainField))
          }
        }
      })
      .catch((error) => {
        if (error?.code !== 'ERR_CANCELED') handleApiError(error, modal)
      })
      .finally(() => setDetailsLoading(false))
  }, [selectedIds])

  const toggleOne = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        const row = rowsRef.current.find((r) => r.id === id)
        if (row) selectedRowsCacheRef.current.set(id, row)
        next.add(id)
      }
      return next
    })
  }, [])

  const removeRow = useCallback((id: string) => {
    selectedRowsCacheRef.current.delete(id)
    setRows((prev) => prev.filter((r) => r.id !== id))
    setSelectedIds((prev) => {
      if (!prev.has(id)) return prev
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }, [])

  const allPageSelected =
    rows.length > 0 && rows.every((r) => selectedIds.has(r.id))
  const somePageSelected =
    rows.some((r) => selectedIds.has(r.id)) && !allPageSelected

  const handleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds(new Set())
    } else {
      cacheRows(rows)
      setSelectedIds(new Set(rows.map((r) => r.id)))
    }
  }

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as ActiveTab)
    if (tab === 'ADDITIONAL' || tab === 'MAIN') {
      setSelectedMainField(null)
      setSelectedChrcCode(null)
      setProcessValue('')
      setProcessValues([])
      resetSlottedDraft()
      setAction('SET')
      setDynamicColumnLabel('Выбранный реквизит')
      setDictionaryValues([])
    }
  }

  const handleMainFieldSelect = (key: string) => {
    setSelectedMainField(key)
    setProcessValue('')
    setProcessValues([])
    setDictionaryValues([])

    const field = MAIN_FIELDS.find((f) => f.key === key)
    setDynamicColumnLabel(field?.label ?? key)

    switch (field?.type) {
      case 'REFERENCE_BRAND':
        loadBrands()
        break
      case 'REFERENCE_COUNTRY':
        loadCountries()
        break
      case 'REFERENCE_UNIT':
        loadUnits()
        break
      case 'REFERENCE_CATEGORY':
        loadCategories()
        break
      case 'REFERENCE_PARENT':
        loadParents()
        break
    }

    if (selectedDetails.length === 1) {
      const model = selectedDetails[0].nomenclatureExtendedModel
      if (field?.type.startsWith('REFERENCE_')) {
        setProcessValue(getMainFieldIdValue(model, key))
      } else {
        setProcessValue(getMainFieldDisplayValue(model, key))
      }
      setProcessValues(getMainFieldListValues(model, key))
    }
  }

  const intersectedCharacteristics = useMemo((): Characteristic[] => {
    if (selectedDetails.length === 0) return []
    if (selectedDetails.length === 1) return selectedDetails[0].characteristics
    const first = selectedDetails[0].characteristics
    return first.filter((c) =>
      selectedDetails.every((d) =>
        d.characteristics.some((dc) => dc.chrcCode === c.chrcCode)
      )
    )
  }, [selectedDetails])

  const handleChrcSelect = useCallback(
    async (code: number, name: string) => {
      setSelectedChrcCode(code)
      setProcessValue('')
      setProcessValues([])
      resetSlottedDraft()
      const requestId = ++chrcSelectRequestRef.current
      const isCurrentRequest = () => requestId === chrcSelectRequestRef.current
      setDynamicColumnLabel(name)
      setDictionaryValues([])

      const chrc = intersectedCharacteristics.find((c) => c.chrcCode === code)

      const singleSelectedChrc =
        selectedDetails.length === 1
          ? selectedDetails[0].characteristics.find((c) => c.chrcCode === code)
          : undefined
      // Для REFERENCE бэкенд отдаёт готовый UUID в chrcValueId, текст —
      // в chrcValue. Берём id, текст оставляем запасным вариантом.
      const prefillUuid =
        singleSelectedChrc?.chrcValueId ?? singleSelectedChrc?.chrcValue ?? ''

      const isSlotted = isSlottedCharacteristicType(chrc?.valueType)

      // Слотовая характеристика заполняется из того же справочника значений,
      // что и REFERENCE (записка §5 п.4: «единый справочник допустимых диаметров»),
      // поэтому справочник грузим и для неё.
      if (isSlotted) {
        // Записка §1: количество полей определяется СТРОГО по slotsCount.
        // При разнородном выборе берём максимум — бэкенд валидирует каждый
        // товар изолированной транзакцией (записка §4b), поэтому товар с
        // меньшим лимитом упадёт с characteristic.slotted.limit.exceeded,
        // а остальные сохранятся. Предупреждаем об этом в интерфейсе.
        const declaredCounts = selectedDetails.map((detail) =>
          getSlotsCount(
            detail.characteristics.find((c) => c.chrcCode === code) ?? {}
          )
        )
        // Ни у одного выбранного товара категория не задала лимит — бэкенд
        // прислал `slotsCount: null` (записка «Значение по умолчанию для
        // slotsCount»). Рендерим поля динамически вместо жёсткого лимита.
        const isDynamic = declaredCounts.every((count) => count === 0)

        const slotCounts = selectedDetails.map((detail) =>
          resolveSlotsCount(
            detail.characteristics.find((c) => c.chrcCode === code) ?? {}
          )
        )
        const resolvedMax = slotCounts.length ? Math.max(...slotCounts) : 0
        const slotsCount = isDynamic
          ? getDynamicSlotsCount(resolvedMax)
          : resolvedMax

        setProcessSlotsCount(slotsCount)
        setProcessSlotsDynamic(isDynamic)
        setProcessSlotsMismatch(new Set(declaredCounts).size > 1)

        // Записка §4a: в форме держим valueId, а не текстовые литералы.
        // slots разреженный — раскладываем по index (§5 п.5).
        const prefill = slotsToFormValueIds(
          singleSelectedChrc?.slots,
          slotsCount
        )

        setProcessSlotValues(
          Array.from({ length: slotsCount }, (_, i) => prefill[i] ?? '')
        )

        // Справочник грузится асинхронно, а в форме уже лежат UUID. Чтобы
        // селекты и превью не показывали «сырые» идентификаторы, сразу кладём
        // в справочник те значения, которые пришли вместе со слотами товара.
        const seeded = (singleSelectedChrc?.slots ?? [])
          .filter((slot) => slot.valueId && slot.value)
          .map((slot) => ({ id: slot.valueId, value: slot.value }))
        if (seeded.length) setDictionaryValues(seeded)
      }

      if (chrc?.valueType === 'REFERENCE' || isSlotted) {
        setDictionaryLoading(true)
        try {
          const chrcUuid =
            chrc?.chrcId ??
            (availableCharacteristicsRef.current.length > 0
              ? availableCharacteristicsRef.current.find((c) => c.code === code)
                  ?.id
              : (await loadCharacteristicsTree()).find((c) => c.code === code)
                  ?.id)
          if (chrcUuid && isCurrentRequest()) {
            const res = await api.get<CharacteristicValuesResponse>(
              `/main/characteristics/${chrcUuid}`
            )
            if (!isCurrentRequest()) return
            const items: DictionaryValue[] = (res.data.items ?? []).map(
              (d) => ({ id: d.id, value: d.value })
            )
            setDictionaryValues(items)
            if (!isSlotted && prefillUuid) {
              // prefillUuid обычно уже UUID (chrcValueId); подстраховываемся
              // сопоставлением по тексту на случай старого ответа без поля.
              const match =
                items.find((d) => d.id === prefillUuid) ??
                items.find((d) => d.value === prefillUuid)
              if (match) setProcessValue(match.id)
            }
          }
        } catch (error) {
          if (isCurrentRequest()) handleApiError(error, modal)
        } finally {
          if (isCurrentRequest()) setDictionaryLoading(false)
        }
      } else if (prefillUuid) {
        setProcessValue(prefillUuid)
      }
    },
    [intersectedCharacteristics, selectedDetails]
  )

  const selectedRows = useMemo(() => {
    const rowsById = new Map(rows.map((row) => [row.id, row]))
    return Array.from(selectedIds)
      .map((id) => selectedRowsCacheRef.current.get(id) ?? rowsById.get(id))
      .filter((row): row is NomenclatureRow => Boolean(row))
  }, [rows, selectedIds])
  const selectedPreviewRows = selectedRows

  const isPanelLoading = detailsLoading
  const isRightPanelDisabled = selectedIds.size === 0
  const currentMainField = MAIN_FIELDS.find((f) => f.key === selectedMainField)
  const isClearDisabled =
    activeTab === 'MAIN'
      ? !currentMainField?.clearable || !!currentMainField?.readonly
      : false
  const isSetDisabled =
    activeTab === 'MAIN' ? !!currentMainField?.readonly : false
  const isListFieldSelected =
    activeTab === 'MAIN' && currentMainField?.type === 'STRING_LIST'
  const cleanedProcessValues = cleanSupplierArticles(processValues)
  const supplierArticleOptions = isListFieldSelected
    ? collectSupplierArticles(
        selectedDetails.map((detail) =>
          getDisplaySupplierArticles(detail.nomenclatureExtendedModel)
        )
      )
    : []
  const isRemovingSomeSupplierArticles =
    isListFieldSelected &&
    action === 'CLEAR' &&
    cleanedProcessValues.length < supplierArticleOptions.length
  const processValuesError =
    isListFieldSelected && action !== 'CLEAR'
      ? getSupplierArticlesError(processValues)
      : null
  const isBarcodeInvalid =
    activeTab === 'MAIN' &&
    selectedMainField === 'BARCODE' &&
    action === 'SET' &&
    getBarcodeError(processValue) !== null

  const selectedChrc =
    activeTab === 'ADDITIONAL' && selectedChrcCode !== null
      ? intersectedCharacteristics.find((c) => c.chrcCode === selectedChrcCode)
      : undefined
  const isSlottedChrcSelected = isSlottedCharacteristicType(
    selectedChrc?.valueType
  )

  /**
   * Записка §4a: значений должно быть от 1 до slotsCount, заполнять
   * последовательно, пропуски запрещены. Проверяем на фронте, чтобы не ловить
   * characteristic.slotted.array.empty / .gaps.not.allowed от бэкенда.
   */
  // Лимит берём тот же, по которому отрисована форма (максимум по выбранным
  // товарам). Иначе payload обрезался бы по первому товару, а пользователь
  // видел бы больше полей, чем реально отправится.
  const slottedPayloadValues = isSlottedChrcSelected
    ? buildSlotValuesPayload(processSlotValues, processSlotsCount)
    : null
  const slottedGapIndex = isSlottedChrcSelected
    ? findSlotGap(processSlotValues)
    : 0

  const isProcessDisabled =
    isRightPanelDisabled ||
    detailsLoading ||
    (activeTab === 'MAIN' && !selectedMainField) ||
    (activeTab === 'ADDITIONAL' && selectedChrcCode === null) ||
    (action !== 'CLEAR' &&
      (isListFieldSelected
        ? cleanedProcessValues.length === 0
        : isSlottedChrcSelected
          ? !slottedPayloadValues
          : !processValue)) ||
    isBarcodeInvalid ||
    processValuesError !== null ||
    (action === 'CLEAR' && isClearDisabled) ||
    (isListFieldSelected &&
      action === 'CLEAR' &&
      cleanedProcessValues.length === 0) ||
    !!currentMainField?.readonly

  const usedChrcCodes = useMemo(
    () => new Set(activeChrcFilters.map((f) => f.code)),
    [activeChrcFilters]
  )

  const detailsById = useMemo(
    () => new Map(selectedDetails.map((d) => [d.id, d])),
    [selectedDetails]
  )
  const computeDynamicCellValue = useCallback(
    (row: NomenclatureRow): string => {
      if (activeTab === 'MAIN' && selectedMainField) {
        const details = detailsById.get(row.id)
        if (details) {
          return getMainFieldDisplayValue(
            details.nomenclatureExtendedModel,
            selectedMainField
          )
        }
        switch (selectedMainField) {
          case 'DESCRIPTION':
            return row.description
          case 'ARTICLE':
            return row.article
          case 'BRAND':
            return row.brandName
          case 'CATEGORY':
            return row.categoryName
          case 'UNIT_OF_MEASUREMENT':
            return row.unitName
          case 'COUNTRY_OF_ORIGIN':
            return row.countryName
          case 'PARENT_ID':
            return row.parentName
          case 'CODE':
            return String(row.code)
          default:
            return row.dynamicValue
        }
      }
      if (activeTab === 'ADDITIONAL' && selectedChrcCode !== null) {
        const details = detailsById.get(row.id)
        if (details) {
          const char = details.characteristics.find(
            (c) => c.chrcCode === selectedChrcCode
          )
          const raw = char?.chrcValue ?? ''

          if (isSlottedCharacteristicType(char?.valueType)) {
            // Записка §1: строка вида «32 × 25 × 40 × 32» собирается из slots
            return formatSlotsValue(char?.slots) || raw
          }
          if (char?.valueType === 'REFERENCE') {
            // chrcValue у REFERENCE — уже текст значения; chrcValueId — на всякий
            return (
              raw ||
              dictionaryValues.find((d) => d.id === char?.chrcValueId)?.value ||
              ''
            )
          }
          if (raw === 'true') return 'Да'
          if (raw === 'false') return 'Нет'
          return raw
        }
        return row.dynamicValue
      }
      return row.dynamicValue
    },
    [
      activeTab,
      selectedMainField,
      selectedChrcCode,
      detailsById,
      dictionaryValues,
    ]
  )

  const sortedRows = useMemo(() => {
    if (!dynamicSortDir) return rows
    const dynamicSortable =
      (activeTab === 'MAIN' && selectedMainField !== null) ||
      (activeTab === 'ADDITIONAL' && selectedChrcCode !== null)
    if (!dynamicSortable) return rows
    const result = [...rows]
    const selectedIndices: number[] = []
    rows.forEach((r, i) => {
      if (selectedIds.has(r.id)) selectedIndices.push(i)
    })
    if (selectedIndices.length < 2) return rows
    const selectedItems = selectedIndices.map((i) => rows[i])
    const sorted = [...selectedItems].sort((a, b) => {
      const va = computeDynamicCellValue(a)
      const vb = computeDynamicCellValue(b)
      const cmp = va.localeCompare(vb, 'ru', {
        sensitivity: 'base',
        numeric: true,
      })
      return dynamicSortDir === 'asc' ? cmp : -cmp
    })
    selectedIndices.forEach((origIdx, i) => {
      result[origIdx] = sorted[i]
    })
    return result
  }, [
    rows,
    dynamicSortDir,
    selectedIds,
    computeDynamicCellValue,
    activeTab,
    selectedMainField,
    selectedChrcCode,
  ])

  const handleRowClick = useCallback(
    (id: string, index: number, e: React.MouseEvent) => {
      if (e.ctrlKey || e.metaKey) {
        toggleOne(id)
        lastClickedIndex.current = index
      } else if (e.shiftKey && lastClickedIndex.current !== null) {
        const start = Math.min(lastClickedIndex.current, index)
        const end = Math.max(lastClickedIndex.current, index)
        const rangeRows = sortedRows.slice(start, end + 1)
        cacheRows(rangeRows)
        const rangeIds = rangeRows.map((r) => r.id)
        setSelectedIds((prev) => {
          const next = new Set(prev)
          rangeIds.forEach((rid) => next.add(rid))
          return next
        })
      } else {
        const row = sortedRows[index]
        if (row) selectedRowsCacheRef.current.set(row.id, row)
        setSelectedIds((prev) => {
          if (prev.size === 1 && prev.has(id)) return prev
          return new Set([id])
        })
        lastClickedIndex.current = index
      }
    },
    [sortedRows, toggleOne, cacheRows]
  )

  const handleProcess = async () => {
    if (isProcessDisabled || processing) return

    const submitAction: MassUpdateAction = isRemovingSomeSupplierArticles
      ? 'REMOVE'
      : action
    const getNextSupplierArticles = (
      model: Parameters<typeof getDisplaySupplierArticles>[0]
    ) =>
      applySupplierArticlesAction(
        getDisplaySupplierArticles(model),
        submitAction,
        cleanedProcessValues
      )

    const nomenclatureIds = selectedRows.map((r) => r.id)

    // NUMERIC на бэке идёт через value.toDouble() — запятая уронила бы товар
    // с nomenclature.update.type.cast.error. Нормализуем здесь.
    const isNumericAdditional =
      activeTab === 'ADDITIONAL' && selectedChrc?.valueType === 'NUMERIC'
    const isBarcodeField =
      activeTab === 'MAIN' && selectedMainField === 'BARCODE'
    const isArticleField =
      activeTab === 'MAIN' && selectedMainField === 'ARTICLE'
    const normalizedValue = isBarcodeField
      ? sanitizeBarcode(processValue)
      : isArticleField
        ? processValue.trim()
        : isNumericAdditional && typeof processValue === 'string'
          ? processValue.replace(',', '.')
          : processValue

    const payload: MassUpdateRequest = {
      nomenclatureIds,
      action: submitAction,
      value: submitAction === 'CLEAR' ? null : normalizedValue,
    }

    // Записка §4b: для SLOTTED_REFERENCE отправляем values вместо value.
    // §4c: очистка идёт action: "CLEAR" вообще без values.
    if (isSlottedChrcSelected) {
      delete payload.value
      if (submitAction !== 'CLEAR') {
        if (!slottedPayloadValues) return
        payload.values = slottedPayloadValues
      }
    }

    if (isListFieldSelected) {
      delete payload.value
      if (submitAction !== 'CLEAR') payload.values = cleanedProcessValues
    }

    if (activeTab === 'MAIN') {
      payload.fieldType = 'MAIN'
      payload.mainField = selectedMainField
      payload.chrcCode = null
    } else {
      payload.fieldType = 'ADDITIONAL'
      payload.mainField = null
      payload.chrcCode = selectedChrcCode
    }

    // ТЗ п.3.4: массовый перенос товаров в другую категорию может обрезать
    // слотовые характеристики, не влезающие в лимит новой категории. Бэкенд
    // анализирует влияние только для этого случая (MAIN + CATEGORY + SET);
    // для остальных полей preview вернёт hasImpact: false.
    if (
      activeTab === 'MAIN' &&
      selectedMainField === 'CATEGORY' &&
      submitAction === 'SET'
    ) {
      const decision = await confirmSlottedImpact({
        previewUrl: '/main/nomenclatures/mass-update/preview',
        body: payload,
        modal,
      })
      if (decision && !decision.proceed) return
    }

    setProcessing(true)
    try {
      const response = await api.patch(
        '/main/nomenclatures/mass-update',
        payload
      )
      const results: {
        nomenclatureId: string
        status: string
        errorMessage?: string
      }[] = response.data.results ?? []
      let cellValue = ''
      if (submitAction !== 'CLEAR') {
        if (isListFieldSelected) {
          cellValue = cleanedProcessValues.join(', ')
        } else if (isSlottedChrcSelected) {
          // В форме лежат valueId (записка §4a) — для показа разворачиваем
          // их в текст по справочнику. Итоговую строку всё равно считает бэкенд,
          // здесь только оптимистичное обновление таблицы.
          cellValue = formatSlotValuesList(
            (slottedPayloadValues ?? []).map(
              (valueId) =>
                dictionaryValues.find((d) => d.id === valueId)?.value ?? valueId
            )
          )
        } else if (
          activeTab === 'ADDITIONAL' &&
          intersectedCharacteristics.find(
            (c) => c.chrcCode === selectedChrcCode
          )?.valueType === 'REFERENCE'
        ) {
          cellValue =
            dictionaryValues.find((d) => d.id === processValue)?.value ??
            processValue
        } else if (
          activeTab === 'MAIN' &&
          currentMainField?.type.startsWith('REFERENCE_')
        ) {
          if (currentMainField.type === 'REFERENCE_CATEGORY') {
            cellValue =
              findNodeById(globalCategoriesTree, processValue)?.category
                ?.description ?? processValue
          } else if (currentMainField.type === 'REFERENCE_PARENT') {
            cellValue =
              findNodeById(globalGroupsTree, processValue)?.nomenclatureGroup
                ?.description ?? processValue
          } else {
            const refMap: Record<string, SimpleEntity[]> = {
              REFERENCE_BRAND: brands,
              REFERENCE_UNIT: units,
              REFERENCE_COUNTRY: countries,
            }
            const list = refMap[currentMainField.type] ?? []
            cellValue =
              list.find((e) => e.id === processValue)?.description ??
              processValue
          }
        } else if (processValue === 'true') {
          cellValue = 'Да'
        } else if (processValue === 'false') {
          cellValue = 'Нет'
        } else {
          cellValue =
            isBarcodeField || isArticleField ? normalizedValue : processValue
        }
      }

      setRows((prev) =>
        prev.map((row) => {
          const result = results.find((r) => r.nomenclatureId === row.id)
          if (!result) return row

          if (result.status === 'SUCCESS') {
            setTimeout(() => {
              setRows((r) =>
                r.map((rr) =>
                  rr.id === row.id ? { ...rr, flashStatus: null } : rr
                )
              )
            }, 1500)
            const searchFieldPatch: Partial<NomenclatureRow> = {}
            if (activeTab === 'MAIN') {
              if (selectedMainField === 'DESCRIPTION')
                searchFieldPatch.description = cellValue
              else if (selectedMainField === 'ARTICLE')
                searchFieldPatch.article = cellValue
              else if (selectedMainField === 'BRAND')
                searchFieldPatch.brandName = cellValue
              else if (selectedMainField === 'CATEGORY')
                searchFieldPatch.categoryName = cellValue
              else if (selectedMainField === 'UNIT_OF_MEASUREMENT')
                searchFieldPatch.unitName = cellValue
              else if (selectedMainField === 'COUNTRY_OF_ORIGIN')
                searchFieldPatch.countryName = cellValue
              else if (selectedMainField === 'PARENT_ID')
                searchFieldPatch.parentName = cellValue
            }
            const updatedRow: NomenclatureRow = {
              ...row,
              ...searchFieldPatch,
              dynamicValue: isListFieldSelected
                ? getNextSupplierArticles(
                    selectedDetails.find((detail) => detail.id === row.id)
                      ?.nomenclatureExtendedModel ?? {}
                  ).join(', ')
                : cellValue,
              flashStatus: 'success',
              errorMessage: null,
            }
            selectedRowsCacheRef.current.set(row.id, updatedRow)
            return updatedRow
          }

          if (result.status === 'FAILURE') {
            setTimeout(() => {
              setRows((r) =>
                r.map((rr) =>
                  rr.id === row.id ? { ...rr, flashStatus: null } : rr
                )
              )
            }, 1500)
            const updatedRow: NomenclatureRow = {
              ...row,
              flashStatus: 'error',
              errorMessage: result.errorMessage ?? 'Ошибка',
            }
            selectedRowsCacheRef.current.set(row.id, updatedRow)
            return updatedRow
          }

          return row
        })
      )

      const successCount = results.filter((r) => r.status === 'SUCCESS').length
      const failResults = results.filter((r) => r.status === 'FAILURE')
      const uniqueFailureMessages = [
        ...new Set(failResults.map((r) => r.errorMessage).filter(Boolean)),
      ]
      const fieldLabel =
        activeTab === 'MAIN'
          ? (currentMainField?.label ?? selectedMainField ?? '')
          : (intersectedCharacteristics.find(
              (c) => c.chrcCode === selectedChrcCode
            )?.chrcName ?? '')

      if (successCount > 0 && failResults.length === 0) {
        if (submitAction === 'CLEAR') {
          toast.success(
            `«${fieldLabel}» очищен для ${successCount} номенклатур`
          )
        } else if (submitAction === 'REMOVE') {
          toast.success(
            `«${fieldLabel}»: удалено «${cellValue}» для ${successCount} номенклатур`
          )
        } else if (submitAction === 'ADD') {
          toast.success(
            `«${fieldLabel}»: добавлено «${cellValue}» для ${successCount} номенклатур`
          )
        } else {
          toast.success(
            `«${fieldLabel}» → «${cellValue}» установлено для ${successCount} номенклатур`
          )
        }
      } else if (successCount > 0 && failResults.length > 0) {
        toast.warning(
          `Выполнено: ${successCount}, ошибок: ${failResults.length}`,
          { description: uniqueFailureMessages.slice(0, 3).join(' ') }
        )
      } else if (successCount === 0 && failResults.length > 0) {
        const uniqueErrors = uniqueFailureMessages
        if (uniqueErrors.length === 1) {
          toast.error(`Ошибка: ${uniqueErrors[0]}`)
        } else {
          toast.error(
            `Все ${failResults.length} операций завершились с ошибкой`
          )
          uniqueErrors.slice(0, 3).forEach((msg) => toast.error(msg ?? ''))
        }
      }

      const successIds = new Set(
        results
          .filter((r) => r.status === 'SUCCESS')
          .map((r) => r.nomenclatureId)
      )
      if (successIds.size > 0) {
        if (isListFieldSelected && submitAction !== 'SET') setProcessValues([])
        const newRawValue = submitAction === 'CLEAR' ? '' : processValue
        setSelectedDetails((prev) =>
          prev.map((detail) => {
            if (!successIds.has(detail.id)) return detail
            if (activeTab === 'ADDITIONAL' && selectedChrcCode !== null) {
              return {
                ...detail,
                characteristics: detail.characteristics.map((c) => {
                  if (c.chrcCode !== selectedChrcCode) return c
                  if (!isSlottedCharacteristicType(c.valueType)) {
                    return { ...c, chrcValue: newRawValue }
                  }

                  // Записка §1: slots — только фактически заполненные,
                  // разреженный массив. slotsCount при этом не меняется.
                  const applied =
                    submitAction === 'CLEAR' ? [] : (slottedPayloadValues ?? [])
                  return {
                    ...c,
                    chrcValue: cellValue,
                    slots: applied.map((valueId, i) => ({
                      index: i + 1,
                      valueId,
                      value:
                        dictionaryValues.find((d) => d.id === valueId)?.value ??
                        '',
                    })),
                  }
                }),
              }
            }
            if (activeTab === 'MAIN' && selectedMainField) {
              const model = { ...detail.nomenclatureExtendedModel }
              switch (selectedMainField) {
                case 'DESCRIPTION':
                  model.description = cellValue
                  break
                case 'ARTICLE':
                  model.articleOriginal = cellValue
                  model.article = toNormalizedArticle(cellValue)
                  break
                case 'SUPPLIER_ARTICLES': {
                  const nextArticles = getNextSupplierArticles(model)
                  model.supplierArticlesOriginal = nextArticles
                  model.supplierArticles = nextArticles.map(toNormalizedArticle)
                  break
                }
                case 'BARCODE':
                  model.barcode = cellValue
                  break
                case 'COMMENT':
                  model.comment = cellValue
                  break
                case 'DETAILS':
                  model.details = cellValue
                  break
                case 'BRAND':
                  model.brandDto = newRawValue
                    ? { id: newRawValue, description: cellValue }
                    : undefined
                  break
                case 'CATEGORY':
                  model.categoryDto = newRawValue
                    ? { id: newRawValue, description: cellValue }
                    : undefined
                  break
                case 'UNIT_OF_MEASUREMENT':
                  model.unitOfMeasurementDto = newRawValue
                    ? { id: newRawValue, description: cellValue }
                    : undefined
                  break
                case 'COUNTRY_OF_ORIGIN':
                  model.countryDto = newRawValue
                    ? { id: newRawValue, description: cellValue }
                    : undefined
                  break
                case 'PARENT_ID':
                  model.parentModel = newRawValue
                    ? { id: newRawValue, description: cellValue }
                    : undefined
                  break
                case 'ITEM_LENGTH':
                  model.itemLength = newRawValue ? Number(newRawValue) : 0
                  break
                case 'ITEM_WIDTH':
                  model.itemWidth = newRawValue ? Number(newRawValue) : 0
                  break
                case 'ITEM_HEIGHT':
                  model.itemHeight = newRawValue ? Number(newRawValue) : 0
                  break
                case 'ITEM_WEIGHT':
                  model.itemWeight = newRawValue ? Number(newRawValue) : 0
                  break
                case 'WARRANTY':
                  model.warranty = newRawValue ? Number(newRawValue) : 0
                  break
                case 'IS_DISCONTINUED':
                  model.isDiscontinued = newRawValue === 'true'
                  break
                case 'IS_FILLED':
                  model.isFilled = newRawValue === 'true'
                  break
                case 'MULTIPLICITY':
                  model.multiplicity = newRawValue
                    ? newRawValue === 'true'
                    : undefined
                  break
              }
              return { ...detail, nomenclatureExtendedModel: model }
            }
            return detail
          })
        )
      }
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setProcessing(false)
    }
  }

  const addPendingFiles = (
    files: FileList | File[] | null,
    attachType: AttachmentType = 'IMAGE'
  ) => {
    if (!files || files.length === 0) return
    const fileArray = Array.from(files)
    setPendingFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.file.name))
      const duplicates: string[] = []
      const unique = fileArray.filter((f) => {
        if (existingNames.has(f.name)) {
          duplicates.push(f.name)
          return false
        }
        return true
      })
      if (duplicates.length > 0) {
        toast.warning(`Уже добавлено: ${duplicates.join(', ')}`)
      }
      if (unique.length === 0) return prev
      const hadImages = prev.some((f) => f.type === 'IMAGE')
      const newFiles: PendingFile[] = unique.map((file) => ({
        id: `${Date.now()}-${Math.random()}`,
        file,
        type: attachType,
        previewUrl:
          attachType === 'IMAGE' ? URL.createObjectURL(file) : undefined,
      }))
      const firstNewImage = newFiles.find((f) => f.type === 'IMAGE')
      if (!hadImages && firstNewImage) {
        setMainPendingImageId(firstNewImage.id)
      }
      return [...prev, ...newFiles]
    })
  }

  const removePendingFile = (id: string) => {
    setPendingFiles((prev) => {
      const file = prev.find((f) => f.id === id)
      if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl)
      const next = prev.filter((f) => f.id !== id)
      setMainPendingImageId((cur) => {
        if (cur !== id) return cur
        const nextImage = next.find((f) => f.type === 'IMAGE')
        return nextImage?.id ?? null
      })
      return next
    })
  }

  const MAX_UPLOAD_MB = 25
  const uploadPendingFiles = async (scope: 'IMAGES' | 'DOCUMENTS') => {
    const filesToUpload = pendingFiles.filter((f) =>
      scope === 'IMAGES' ? f.type === 'IMAGE' : f.type !== 'IMAGE'
    )
    if (filesToUpload.length === 0) return
    if (selectedDetails.length === 0) return

    const totalBytes = filesToUpload.reduce((sum, f) => sum + f.file.size, 0)
    const totalMB = totalBytes / (1024 * 1024)
    if (totalMB > MAX_UPLOAD_MB) {
      toast.error(
        `Общий размер файлов (${totalMB.toFixed(1)} МБ) превышает лимит ${MAX_UPLOAD_MB} МБ. Уменьшите количество файлов.`
      )
      return
    }

    const productCodes = selectedDetails.map((d) => d.code)
    const metadata = filesToUpload.map((pf) => ({
      originalFileName: pf.file.name,
      type: pf.type,
      isMain: pf.type === 'IMAGE' ? pf.id === mainPendingImageId : false,
    }))

    // Convert main image to data URL before upload so we can show it immediately after
    let mainImageDataUrl: string | null = null
    if (scope === 'IMAGES') {
      const mainFile = filesToUpload.find(
        (f) => f.type === 'IMAGE' && f.id === mainPendingImageId
      )
      if (mainFile) {
        mainImageDataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.onerror = () => reject()
          reader.readAsDataURL(mainFile.file)
        }).catch(() => null)
      }
    }

    const formData = new FormData()
    formData.append(
      'metadata',
      new Blob([JSON.stringify({ productCodes, metadata })], {
        type: 'application/json',
      })
    )
    filesToUpload.forEach((pf) => formData.append('files', pf.file))

    const uploadedIds = new Set(filesToUpload.map((f) => f.id))

    setUploading(true)
    try {
      const res = await api.post('/nomenclatures/attachments', formData)
      toast.success(res.data?.message || 'Файлы успешно загружены')
      setPendingFiles((prev) => {
        const remaining = prev.filter((f) => !uploadedIds.has(f.id))
        prev
          .filter((f) => uploadedIds.has(f.id))
          .forEach((f) => {
            if (f.previewUrl) URL.revokeObjectURL(f.previewUrl)
          })
        return remaining
      })
      if (scope === 'IMAGES') {
        setMainPendingImageId(null)
        if (mainImageDataUrl) {
          const mainFile = filesToUpload.find(
            (f) => f.type === 'IMAGE' && f.id === mainPendingImageId
          )
          const ext =
            mainFile?.file.name.split('.').pop()?.toLowerCase() ?? 'jpg'
          productCodes.forEach((code) => {
            updateImageCache(code, [
              { url: mainImageDataUrl!, extension: ext, isMain: true },
            ])
            clearImageCache(code, 'all')
          })
          setImageVersion((v) => v + 1)
        }
      }
      setAttachDocsFetched(false)
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 413) {
        toast.error(
          'Файлы слишком большие. Попробуйте загрузить меньше файлов за раз.'
        )
      } else {
        handleApiError(error, modal)
      }
    } finally {
      setUploading(false)
    }
  }

  const deleteAttachFile = async (
    fileName: string,
    fileType: AttachmentType
  ) => {
    const code =
      selectedIds.size === 1 && selectedDetails.length === 1
        ? selectedDetails[0].code
        : null
    if (!code) return
    try {
      const res = await api.post('/nomenclatures/attachments/delete', {
        productCode: code,
        files: [{ fileName, type: fileType }],
      })
      toast.success(res.data?.message || 'Файл удалён')
      clearImageCache(code)
      setImageVersion((v) => v + 1)
      if (fileType !== 'IMAGE') {
        setAttachDocs((prev) => prev.filter((f) => f.name !== fileName))
      }
    } catch (error) {
      handleApiError(error, modal)
    }
  }

  const updateAttachFileType = async (
    fileName: string,
    fileExtension: string,
    newType: AttachmentType
  ) => {
    const code =
      selectedIds.size === 1 && selectedDetails.length === 1
        ? selectedDetails[0].code
        : null
    if (!code) return
    const normalizedExtension = fileExtension.replace(/^\./, '').toLowerCase()
    const fileNameWithExtension =
      normalizedExtension &&
      !fileName.toLowerCase().endsWith(`.${normalizedExtension}`)
        ? `${fileName}.${normalizedExtension}`
        : fileName
    try {
      const res = await api.patch('/nomenclatures/attachments/types', {
        productCode: code,
        changes: [{ fileName: fileNameWithExtension, newType }],
      })
      toast.success(res.data?.message || 'Тип файла изменён')
      clearImageCache(code)
      setImageVersion((v) => v + 1)
      setAttachDocs((prev) =>
        prev.map((f) => (f.name === fileName ? { ...f, type: newType } : f))
      )
    } catch (error) {
      handleApiError(error, modal)
    }
  }

  const downloadAttachFile = (file: AttachFile) => {
    try {
      const cleanBase64 = file.dvdan.replace(/[^A-Za-z0-9+/=]/g, '')
      const mimeType = file.ContentType || 'application/octet-stream'
      const link = document.createElement('a')
      link.href = `data:${mimeType};base64,${cleanBase64}`
      link.download = file.name ? file.name : `file.${file.extension}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      toast.success('Файл скачан')
    } catch {
      toast.error('Не удалось скачать файл')
    }
  }

  const refFieldLoading = (() => {
    if (activeTab !== 'MAIN' || !currentMainField) return false
    switch (currentMainField.type) {
      case 'REFERENCE_BRAND':
        return brandsLoading
      case 'REFERENCE_COUNTRY':
        return countriesLoading
      case 'REFERENCE_UNIT':
        return unitsLoading
      case 'REFERENCE_CATEGORY':
        return categoriesLoading
      case 'REFERENCE_PARENT':
        return parentsLoading
      default:
        return false
    }
  })()

  const value: AttributeProcessingContextValue = {
    query,
    setQuery,
    loading,
    hasSearched,
    handleSearch,
    showFilters,
    setShowFilters,
    hasActiveFilters,
    barcode,
    setBarcode,
    brandId,
    setBrandId,
    brands,
    brandsLoading,
    loadBrands,
    categoryId,
    setCategoryId,
    categoriesLoading,
    loadCategories,
    parentId,
    setParentId,
    parentsLoading,
    loadParents,
    countryIds,
    setCountryIds,
    countries,
    countriesLoading,
    loadCountries,
    unitIds,
    setUnitIds,
    units,
    unitsLoading,
    loadUnits,
    isDiscontinued,
    setIsDiscontinued,
    isFilled,
    setIsFilled,
    multiplicity,
    setMultiplicity,
    emptyCharacteristicsFilter,
    setEmptyCharacteristicsFilter,
    activeChrcFilters,
    addFilterOpen,
    setAddFilterOpen,
    characteristicsLoading,
    characteristicsLoadedRef,
    availableCharacteristics,
    usedChrcCodes,
    addCharacteristicFilter,
    removeCharacteristicFilter,
    updateChrcFilter,
    resetAllFilters,
    loadCharacteristicsTree,
    refreshCharacteristicsTree,
    refreshCategoriesTree,
    globalCategoriesTree,
    antdCategoriesTreeLeafOnly,
    globalGroupsTree,
    antdParentsTreeLeafOnly,
    rows,
    sortedRows,
    backendSort,
    handleBackendSort,
    dynamicSortDir,
    handleDynamicSort,
    totalRows,
    loadingMore,
    loadMoreRef,
    selectedIds,
    selectedPreviewRows,
    setSelectedIds,
    allPageSelected,
    somePageSelected,
    handleSelectAll,
    handleRowClick,
    toggleOne,
    removeRow,
    computeDynamicCellValue,
    dynamicColumnLabel,
    setDynamicColumnLabel,
    setSelectedDetails,
    activeTab,
    handleTabChange,
    isRightPanelDisabled,
    detailsLoading,
    selectedDetails,
    isPanelLoading,
    selectedMainField,
    handleMainFieldSelect,
    selectedChrcCode,
    intersectedCharacteristics,
    handleChrcSelect,
    action,
    setAction,
    processValue,
    setProcessValue,
    processValues,
    setProcessValues,
    isBarcodeInvalid,
    processValuesError,
    isListFieldSelected,
    supplierArticleOptions,
    processSlotValues,
    setProcessSlotValues,
    updateSlotValue,
    processSlotsCount,
    processSlotsMismatch,
    processSlotsDynamic,
    isSlottedChrcSelected,
    slottedGapIndex,
    currentMainField,
    isClearDisabled,
    isSetDisabled,
    isProcessDisabled,
    processing,
    handleProcess,
    refFieldLoading,
    dictionaryValues,
    dictionaryLoading,
    imageReady,
    setImageReady,
    imageVersion,
    attachDocs,
    attachDocsLoading,
    uploading,
    dragOver,
    setDragOver,
    pendingFiles,
    mainPendingImageId,
    setMainPendingImageId,
    addPendingFiles,
    removePendingFile,
    uploadPendingFiles,
    downloadAttachFile,
    deleteAttachFile,
    updateAttachFileType,
  }

  return (
    <>
      {contextHolder}
      <AttributeProcessingContext.Provider value={value}>
        {children}
      </AttributeProcessingContext.Provider>
    </>
  )
}
